import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {useHistory} from "react-router-dom";
import CreatableSelect from "react-select/creatable";
import {getWorkflowPreviewApi, startWorkflowPreviewApi} from "../../services/tagsAutomationService";

// GOD-TASK-197 Tags Automation: pieces shared by the screens.

export const BASE = `${process.env.PUBLIC_URL}/tags-automation`;

/**
 * can('Create Workflow') for the signed-in user. View permissions are the menu titles
 * ('Workflows', 'Activity Log'); actions are Create / Update / Delete Workflow (PermissionSeeder).
 */
export const useCan = () => {
    const user = useSelector(({authReducer}) => authReducer.data.user);
    return (name) => (user?.permission || []).includes(name);
};

/**
 * Renders the page only when the user holds the permission; otherwise sends them to the
 * permission error page, as the other admin pages do.
 */
export const RequirePermission = ({name, children}) => {
    const can = useCan();
    const history = useHistory();
    const allowed = can(name);
    useEffect(() => {
        if (!allowed) history.push('/error/permission');
    }, [allowed, history]);
    return allowed ? children : null;
};

/**
 * The server's message for a failed call. The API layer rejects with the response body for a 422
 * and with the axios error otherwise.
 */
export const errorMessage = (e) => {
    if (e?.error) return e.error;
    const data = e?.response?.data;
    if (data?.error) return data.error;
    if (data?.message) return data.message;
    return e?.message || 'Something went wrong. Please try again.';
};

/**
 * Searchable multi-select of the existing tags (as Product Tags on the product edit page); a tag
 * that does not exist yet can be typed and added. "a, b, c" adds three tags; duplicates are dropped
 * case-insensitively.
 */
const QUOTES = /["'`“”‘’]/;

export const TagsField = ({tags, onChange, options = [], placeholder, invalid, split = true, noQuotes = false}) => {
    const toOption = (t) => ({value: t, label: t});
    const allowed = (t) => !noQuotes || !QUOTES.test(t);
    const apply = (values) => {
        const next = [];
        // split=false keeps each entry whole (regex patterns may contain commas, e.g. {1,3}).
        values.forEach(v => (split ? String(v).split(',') : [String(v)]).map(t => t.trim()).filter(Boolean).filter(allowed).forEach(t => {
            if (!next.some(x => x.toLowerCase() === t.toLowerCase())) next.push(t);
        }));
        onChange(next);
    };
    return (
        <CreatableSelect
            isMulti
            placeholder={placeholder}
            value={tags.map(toOption)}
            options={options.filter(allowed).map(toOption)}
            onChange={selected => apply((selected || []).map(o => o.value))}
            onCreateOption={input => apply([...tags, input])}
            isValidNewOption={input => input.trim() !== '' && allowed(input)}
            noOptionsMessage={({inputValue}) => (inputValue && !allowed(inputValue) ? 'Tags cannot contain quotes' : 'No options')}
            formatCreateLabel={input => `Add "${input}"`}
            createOptionPosition="first"
            menuPortalTarget={document.body}
            styles={{
                control: (base) => ({...base, ...(invalid ? {borderColor: '#dc3545'} : {})}),
                menuPortal: (base) => ({...base, zIndex: 1060}),
            }}
            components={{IndicatorSeparator: () => null}}
        />
    );
};

/** Read-only tag chips: added tags in the theme's light primary, removed tags in light danger. */
export const TagBadge = ({tag, kind}) => (
    <span className={`badge ta-badge ${kind === 'add' ? 'badge-light-primary' : kind === 'remove' ? 'badge-light-danger' : 'badge-light'}`}>{tag}</span>
);

/**
 * Tag chips that show the first `limit`; "+ N more" expands to every tag and "Show fewer" folds back.
 */
export const TagList = ({tags, kind, limit = 7}) => {
    const [all, setAll] = useState(false);
    const shown = all ? tags : tags.slice(0, limit);
    return (
        <>
            {shown.map(t => <TagBadge key={t} tag={t} kind={kind}/>)}
            {tags.length > limit && (
                <button type="button" className="btn btn-link btn-sm p-0 f-12" onClick={() => setAll(!all)}>
                    {all ? 'Show fewer' : `+ ${tags.length - limit} more`}
                </button>
            )}
        </>
    );
};

/**
 * Starts a preview on the server and polls until the count is ready ("Calculating…" meanwhile).
 * Returns {status: 'idle'|'running'|'done'|'failed', ...result}. A new definition restarts it.
 */
export const usePreview = (definition, enabled) => {
    const [state, setState] = useState({status: 'idle'});
    const key = JSON.stringify(definition);

    useEffect(() => {
        if (!enabled) {
            setState({status: 'idle'});
            return undefined;
        }
        let cancelled = false;   // a newer definition (or leaving the page) supersedes this run
        let timer = null;
        setState({status: 'running'});
        // Wait a moment so typing does not start a calculation per key press.
        timer = setTimeout(() => {
            startWorkflowPreviewApi(JSON.parse(key)).then(res => {
                const poll = () => {
                    if (cancelled) return;
                    getWorkflowPreviewApi(res.token).then(r => {
                        if (cancelled) return;
                        if (r.status === 'running') {
                            timer = setTimeout(poll, 2000);
                        } else {
                            setState(r.status === 'expired' ? {status: 'failed', message: 'The calculation expired.'} : r);
                        }
                    }).catch(() => !cancelled && setState({status: 'failed'}));
                };
                poll();
            }).catch(() => !cancelled && setState({status: 'failed'}));
        }, 800);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [key, enabled]);

    return state;
};

export const number = (n) => (n ?? 0).toLocaleString('en-GB');

/**
 * "IF Product → Number of Variants in Stock is less than or equal to 2".
 */
export const ConditionLine = ({c}) => (
    <div className="d-flex align-items-center flex-wrap" style={{gap: 8}}>
        <span className="f-w-600 f-12" style={{width: 52}}>{c.join}</span>
        <span className="badge ta-badge badge-light-info">{c.variant_match ? (c.variant_match === 'all' ? 'All of ' : 'Any of ') : ''}{c.group} → {c.field_label}</span>
        <span className="f-12">{c.operator_label}</span>
        {c.value_label !== '' && c.value_label !== null && <span className="badge ta-badge badge-light">{c.value_label}</span>}
    </div>
);
